/**
 * Following the duck pattern, actions, constants and reducers are in the same file called module.js
 *
 * See: https://github.com/erikras/ducks-modular-redux
 *
 */
import sortBy from 'lodash/sortBy'
import get from 'lodash/get'

import { call, put, takeEvery, select, take, fork } from 'redux-saga/effects'
import { toastr } from 'react-redux-toastr'
import { getAPIUrl, createAuthenticatedUploader } from 'shared/services/networking'
import { getTokenSelector } from 'admin/pages/Authentication/module'
import getRedirectionRoute from 'shared/services/portalRouteRedirector'
import find from 'lodash/find'

import { getCompanySelector, getAuthenticatedSelector } from 'shared/pages/Company/module'

import { getForm } from 'modules/generators/getFormGenerator'

import { sendUnauthenticatedRequestToAPI, getErrorFormatted } from 'modules/generators/requestsToAPI'
import { addOrReplaceByKey } from 'shared/services/addOrReplace'
import convertIntoFormData from 'shared/services/formData'
import { isFormValid } from 'shared/services/formValidator'
import genericFormFields from 'shared/services/genericFormFields'
import { isInputValid } from 'shared/services/inputValidator'

export const GET_FORM_REQUEST = 'Form/GET_FORM_REQUEST'
export const GET_FORM_SUCCESS = 'Form/GET_FORM_SUCCESS'
export const GET_FORM_ERROR = 'Form/GET_FORM_ERROR'

export const INIT_FORM_INPUT = 'Form/INIT_FORM_INPUT'

export const POST_FORM_REQUEST = 'Form/POST_FORM_REQUEST'
export const POST_FORM_SUCCESS = 'Form/POST_FORM_SUCCESS'
export const POST_FORM_ERROR = 'Form/POST_FORM_ERROR'

export const SET_FORM_INPUT = 'Form/SET_FORM_INPUT'
export const SET_FORM_ATTACHMENT = 'Form/SET_FORM_ATTACHMENT'
export const SET_FORM_TIMESLOT = 'Form/SET_FORM_TIMESLOT'
export const SET_FORM_CAPTCHA = 'Form/SET_FORM_CAPTCHA'

export const SET_IS_LOADING = 'Form/SET_IS_LOADING'
export const SET_UPLOAD_PROGRESS = 'Form/SET_UPLOAD_PROGRESS'
export const SET_SHOW_ERRORS = 'Form/SET_SHOW_ERRORS'

export function getFormRequest (id) {
  return {
    type: GET_FORM_REQUEST,
    payload: id
  }
}

export function getFormSuccess (form) {
  return {
    type: GET_FORM_SUCCESS,
    payload: form
  }
}

export function getFormError (error) {
  return {
    type: GET_FORM_ERROR,
    payload: error
  }
}

export function initFormInput (inputType, name, value, formFieldId) {
  return {
    type: INIT_FORM_INPUT,
    payload: {inputType, name, value, formFieldId}
  }
}

export function postFormRequest (userDiagnostic) {
  return {
    type: POST_FORM_REQUEST,
    payload: userDiagnostic
  }
}

export function postFormSuccess () {
  return {
    type: POST_FORM_SUCCESS
  }
}

export function postFormError (error) {
  return {
    type: POST_FORM_ERROR,
    payload: error
  }
}

export function setFormInput (inputType, name, value, formFieldId) {
  return {
    type: SET_FORM_INPUT,
    payload: {inputType, name, value, formFieldId}
  }
}

export function setFormAttachment (file, inputType, name, value, formFieldId) {
  return {
    type: SET_FORM_ATTACHMENT,
    payload: {file, inputType, name, value, formFieldId}
  }
}

export function setFormTimeslot (timeslot) {
  return {
    type: SET_FORM_TIMESLOT,
    payload: timeslot
  }
}

export function setFormCaptchaKey (captchaKey) {
  return {
    type: SET_FORM_CAPTCHA,
    payload: captchaKey
  }
}

export function setIsLoading () {
  return {
    type: SET_IS_LOADING
  }
}

export function setShowErrors () {
  return {
    type: SET_SHOW_ERRORS
  }
}

export function getFormFieldsSelector (state) {
  return state.form.form && [...genericFormFields(), ...sortBy(state.form.form.form_fields, 'order')]
}

export const getFormSelector = state => state.form.form

export const getInputsSelector = state => state.form.inputs

export const getAttachmentSelector = state => state.form.attachments || []

export const getTimeslotSelector = state => state.form.timeslot

export const getIsLoadingSelector = state => state.form.isLoading

export const getHasErrorSelector = (formField, state) => (
  state.form.showErrors &&
  (
    find(state.form.inputs, {'name': formField.name})
      ? !get(find(state.form.inputs, {'name': formField.name}), 'isValid')
      : formField.is_required
  )
)

export const getDataFormInputsSelector = (state, company, userDiagnostic) => {
  const genericFieldsNames = [
    'firstname',
    'lastname',
    'email',
    'visit'
  ]
  const { attachments, form, captchaKey, inputs, timeslot } = state.form

  let data = {
    form: form.id,
    captcha_key: captchaKey,
    company: company.id,
    ticket_state: form.ticket_state || null,
    ticket_description: form.ticket_description,
    firstname: find(inputs, {name: 'firstname'}).value,
    lastname: find(inputs, {name: 'lastname'}).value,
    email: find(inputs, {name: 'email'}).value,
    attachments: attachments,
    timeslot: timeslot,
    user_diagnostic: userDiagnostic
  }

  let visit = find(inputs, {name: 'visit'})
  if (visit) {
    data.visit = visit.value
  }

  let ticketFields = []

  state.form.inputs.map(input => (
    (genericFieldsNames.indexOf(input.name) === -1) &&
      input.name !== 'attachment' &&
      ticketFields.push({
        form_field: input.formFieldId,
        value: input.value
      })
  )
  )

  data = {
    ...data,
    ticket_fields: JSON.stringify(ticketFields)
  }

  return data
}

export const getRedirectionRouteSelector = (state) => {
  let currentRoute = state.routing.location.pathname
  let redirectionRoute = '/thank-you'
  return getRedirectionRoute(currentRoute, redirectionRoute)
}

export function * watchOnProgress (channel) {
  while (true) {
    const data = yield take(channel)
    yield put({type: SET_UPLOAD_PROGRESS, payload: data})
  }
}

export function * postForm (action) {
  const inputs = yield select(getInputsSelector)
  const form = yield select(getFormSelector)
  const isValid = yield call(isFormValid, inputs, form)
  const userDiagnostic = action.payload

  if (!isValid) {
    yield put(setShowErrors())
  } else {
    yield put(setIsLoading())
    const company = yield select(getCompanySelector)
    const formattedInputs = yield select(getDataFormInputsSelector, company, userDiagnostic)
    const formData = yield call(convertIntoFormData, formattedInputs, ['attachments'])
    const isAuthenticated = yield select(getAuthenticatedSelector)

    const config = {
      method: 'post',
      url: `${getAPIUrl()}/tool/ticket/`,
      headers: {
        'Content-Type': 'multipart/form-data'
      },
      data: formData
    }

    const toastrConfig = {
      error: {title: 'Erreur', message: "Votre message n'a pas été envoyé", useApiResponse: true}
    }

    if (isAuthenticated) {
      const token = yield select(getTokenSelector)
      const [ uploadPromise, channel ] = createAuthenticatedUploader(config, token)
      yield fork(watchOnProgress, channel)

      try {
        yield call(() => uploadPromise)
        yield put(postFormSuccess())
      } catch (error) {
        yield put(postFormError(error))
        yield call(toastr.error, toastrConfig.error.title, getErrorFormatted(toastrConfig, error.response.data))
      }
    } else {
      yield call(
        sendUnauthenticatedRequestToAPI,
        config,
        postFormSuccess,
        postFormError,
        toastrConfig
      )
    }
  }
}

export function * getFormSaga () {
  yield takeEvery(GET_FORM_REQUEST, getForm, getFormSuccess, getFormError)
}

export function * postFormSaga () {
  yield takeEvery(POST_FORM_REQUEST, postForm)
}

const initialState = {
  error: null,
  captchaKey: null,
  form: null,
  inputs: [],
  attachments: null,
  timeslot: null,
  isLoading: false,
  isPosted: false,
  showErrors: false
}

/**
 * Following the duck pattern, the module.js file should export a reducer as a default function
 */
export function reducer (state = initialState, action) {
  switch (action.type) {
    case GET_FORM_REQUEST:
      return {
        ...state,
        form: null,
        error: null,
        isPosted: false
      }
    case GET_FORM_SUCCESS:
      return {
        ...state,
        form: action.payload
      }
    case GET_FORM_ERROR:
      return {
        ...state,
        error: action.payload
      }
    case INIT_FORM_INPUT:
      let data = {
        ...action.payload,
        'isValid': isInputValid(action.payload.inputType, action.payload.value)
      }
      return {
        ...state,
        inputs: addOrReplaceByKey(state.inputs, 'formFieldId', data)
      }
    case POST_FORM_REQUEST:
      return {
        ...state,
        showErrors: false,
        error: null
      }
    case POST_FORM_SUCCESS:
      return {
        ...state,
        isLoading: false,
        showErrors: false,
        isPosted: true
      }
    case POST_FORM_ERROR:
      return {
        ...state,
        isLoading: false,
        showErrors: false,
        error: action.payload
      }
    case SET_FORM_INPUT:
      let data2 = {
        ...action.payload,
        'isValid': isInputValid(action.payload.inputType, action.payload.value)
      }
      return {
        ...state,
        inputs: addOrReplaceByKey(state.inputs, 'formFieldId', data2)
      }
    case SET_FORM_ATTACHMENT:
      let data3 = {
        ...action.payload,
        'isValid': isInputValid(action.payload.inputType, action.payload.value)
      }
      return {
        ...state,
        inputs: addOrReplaceByKey(state.inputs, 'formFieldId', data3),
        attachments: action.payload.file
      }
    case SET_FORM_TIMESLOT:
      return {
        ...state,
        timeslot: action.payload
      }
    case SET_FORM_CAPTCHA:
      return {
        ...state,
        captchaKey: action.payload
      }
    case SET_IS_LOADING:
      return {
        ...state,
        isLoading: true
      }
    case SET_UPLOAD_PROGRESS:
      return {
        ...state,
        uploadProgress: action.payload
      }
    case SET_SHOW_ERRORS:
      return {
        ...state,
        showErrors: true
      }
    default:
      return state
  }
}

export default reducer
